import {
  formatValue,
  renderTextTemplate,
  type DesignNode,
  type Project,
  type TextTemplate,
} from "@proof/core";
import { Button, Control } from "./components";

export function TextTemplateEditor({
  project,
  node,
  onChange,
}: {
  project: Project;
  node: DesignNode;
  onChange: (patch: Partial<DesignNode>) => void;
}) {
  const parts = node.textTemplate!;
  const change = (value: TextTemplate) =>
    onChange({ textTemplate: value.length ? value : [{ text: "" }] });
  const edit = (index: number, part: TextTemplate[number]) =>
    change(parts.map((old, i) => (i === index ? part : old)));
  const move = (index: number, offset: number) => {
    const next = [...parts];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    change(next);
  };
  return (
    <section aria-label="Text template">
      <h4>Multiple variables</h4>
      <p className="muted">
        Variables share their values across layers. Each layer keeps its own
        formatting.
      </p>
      {parts.map((part, index) => (
        <div key={index} className="panel">
          {"fieldId" in part ? (
            <Control label={`Template variable ${index + 1}`}>
              <select
                value={part.fieldId}
                onChange={(event) =>
                  edit(index, { fieldId: event.target.value })
                }
              >
                {project.fields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.label}
                  </option>
                ))}
              </select>
            </Control>
          ) : (
            <details>
              <summary>Text and formatting {index + 1}</summary>
              <Control label={`Template text ${index + 1}`}>
                <textarea
                  rows={3}
                  value={part.text}
                  onChange={(event) =>
                    edit(index, { text: event.target.value })
                  }
                />
              </Control>
            </details>
          )}
          <div className="button-row">
            <Button disabled={index === 0} onClick={() => move(index, -1)}>
              Move fragment {index + 1} up
            </Button>
            <Button
              disabled={index === parts.length - 1}
              onClick={() => move(index, 1)}
            >
              Move fragment {index + 1} down
            </Button>
            <Button onClick={() => change(parts.filter((_, i) => i !== index))}>
              Remove fragment {index + 1}
            </Button>
          </div>
        </div>
      ))}
      <div className="button-row">
        <Button
          disabled={parts.length >= 512}
          onClick={() => change([...parts, { text: "" }])}
        >
          Add text fragment
        </Button>
        <Button
          disabled={!project.fields.length || parts.length >= 512}
          onClick={() => change([...parts, { fieldId: project.fields[0].id }])}
        >
          Add variable fragment
        </Button>
        <Button
          onClick={() =>
            onChange({
              content: renderTextTemplate(
                parts,
                (id) => {
                  const field = project.fields.find((field) => field.id === id);
                  return field ? formatValue(field, field.default) : "";
                },
                node.type === "richtext",
              ),
              textTemplate: undefined,
            })
          }
        >
          Use static text
        </Button>
      </div>
    </section>
  );
}
